import '@shared/helpers/immer-setup.helpers';
import 'mapbox-gl/dist/mapbox-gl.css';
import AxiosConfig from '@client/pages/components/AxiosConfig';
import { installBrowserTranslationGuard } from '@client/helpers/browser-translation.helpers';
import { registerStaleAssetReload } from '@client/helpers/stale-asset-reload.helpers';
import { CssBaseline } from '@mui/material';
import { StyledEngineProvider, ThemeProvider } from '@mui/material/styles';
import {
  NODON_GENERATION_PROJECT_MATCH_PATH,
  NODON_PROJECT_PATH,
  NODON_SHARED_PROJECT_MATCH_PATH,
} from '@shared/constants';
import { GlobalLoader } from '@ui/GlobalLoader';
import { ConfirmProvider } from 'material-ui-confirm';
import React from 'react';
import { createRoot, Root } from 'react-dom/client';
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';

import App from './App';
import AppShared from './AppShared';
import { ErrorBoundary } from './ErrorBoundary';
import { Maintenance } from './pages/Maintenance';
import { NewVersion } from './pages/NewVersion';
import { NotFound } from './pages/NotFound';
import SharedProjectRedirect from './pages/SharedProjectRedirect';
import { AdminAIDashboard } from './pages/AdminAIDashboard';
import { AdminAIInstructions } from './pages/AdminAIInstructions';
import { AdminAIChatTools } from './pages/AdminAIChatTools';
import { AdminAIModels } from './pages/AdminAIModels';
import { AdminAIConversations } from './pages/AdminAIConversations';
import { AdminProjectFileBatches } from './pages/AdminProjectFileBatches';
import { AdminProjectFilePipelineFlow } from './pages/AdminProjectFilePipelineFlow';
import { AdminComponents } from './pages/AdminComponents';
import { AdminSentryTest } from './pages/AdminSentryTest';
import { withAuthenticationRequired } from '@auth0/auth0-react';
import Auth0TokenRefresher from './Auth0TokenRefresher';
import Signup from './pages/Signup';
import Auth0ProviderWithHistory from './providers/Auth0ProviderWithHistory';
import { ConfigProvider } from './providers/ConfigProvider';
import { LocaleProvider } from './providers/LocaleProvider';
import { OverlayHoverTooltipWatcher } from './hooks/use-overlay-hover-tooltips.hook';
import { NodonTheme } from './style/theme';
import { VersionChecker } from './pages/components/VersionChecker';
import { EnvironmentFavicon } from './pages/components/EnvironmentFavicon';
import { Navigate } from 'react-router-dom';

// Also listen in index.html: cached pre-deploy HTML does not include this helper.
registerStaleAssetReload();
// Before React renders, so a translator font around the first text nodes is unwrapped.
installBrowserTranslationGuard();

const ProtectedAdminAIDashboard = withAuthenticationRequired(
  () => (
    <AxiosConfig>
      <Auth0TokenRefresher>
        <AdminAIDashboard />
      </Auth0TokenRefresher>
    </AxiosConfig>
  ),
  { onRedirecting: () => <GlobalLoader.Register text="Signing in" /> },
);

const ProtectedAdminComponents = withAuthenticationRequired(
  () => (
    <AxiosConfig>
      <Auth0TokenRefresher>
        <AdminComponents />
      </Auth0TokenRefresher>
    </AxiosConfig>
  ),
  { onRedirecting: () => <GlobalLoader.Register text="Signing in" /> },
);

const ProtectedAdminSentryTest = withAuthenticationRequired(
  () => (
    <AxiosConfig>
      <Auth0TokenRefresher>
        <AdminSentryTest />
      </Auth0TokenRefresher>
    </AxiosConfig>
  ),
  { onRedirecting: () => <GlobalLoader.Register text="Signing in" /> },
);

const domNode = document.getElementById('root');
if (!domNode) throw new Error('No root element found');
const root: Root =
  window.__reactRoot ?? (window.__reactRoot = createRoot(domNode));

root.render(
  <Router>
    <ErrorBoundary>
      <GlobalLoader.Provider>
        <StyledEngineProvider injectFirst>
          <ThemeProvider theme={NodonTheme}>
            <OverlayHoverTooltipWatcher />
            <ConfirmProvider>
              <CssBaseline />
              <GlobalLoader />
              <ConfigProvider>
                <EnvironmentFavicon />
                <VersionChecker />
                <Auth0ProviderWithHistory>
                  <LocaleProvider>
                    <Routes>
                      <Route path="/signup/*" element={<Signup />} />
                      <Route path="/new-version" element={<NewVersion />} />
                      <Route path="/maintenance" element={<Maintenance />} />
                      <Route path="/not-found" element={<NotFound />} />
                      <Route path="/admin">
                        <Route path="*" element={<ProtectedAdminAIDashboard />}>
                          <Route
                            index
                            element={<Navigate to="ai-comments" replace />}
                          />
                          <Route
                            path="ai-comments"
                            element={<AdminAIInstructions />}
                          />
                          <Route
                            path="ai-chat-tools"
                            element={<AdminAIChatTools />}
                          />
                          <Route path="ai-models" element={<AdminAIModels />} />
                          <Route
                            path="ai-chat-conversations"
                            element={<AdminAIConversations />}
                          />
                          <Route path="processing">
                            <Route
                              index
                              element={<Navigate to="batches" replace />}
                            />
                            <Route
                              path="batches"
                              element={
                                <AdminProjectFileBatches view="batches" />
                              }
                            />
                            <Route
                              path="flowchart"
                              element={<AdminProjectFilePipelineFlow />}
                            />
                            <Route
                              path="pipeline"
                              element={
                                <AdminProjectFileBatches view="ai-pipeline" />
                              }
                            />
                          </Route>
                        </Route>
                        <Route
                          path="components/*"
                          element={<ProtectedAdminComponents />}
                        />
                        <Route
                          path="sentry-test"
                          element={<ProtectedAdminSentryTest />}
                        />
                      </Route>
                      <Route
                        path={NODON_SHARED_PROJECT_MATCH_PATH}
                        element={
                          <AxiosConfig disableAuth>
                            <AppShared />
                          </AxiosConfig>
                        }
                      />
                      <Route
                        path={NODON_GENERATION_PROJECT_MATCH_PATH}
                        element={
                          <AxiosConfig disableAuth>
                            <AppShared />
                          </AxiosConfig>
                        }
                      />
                      <Route
                        path="/shared/:sharingKey"
                        element={
                          <AxiosConfig disableAuth>
                            <SharedProjectRedirect />
                          </AxiosConfig>
                        }
                      />
                      <Route
                        path={NODON_PROJECT_PATH}
                        element={
                          <AxiosConfig>
                            <App />
                          </AxiosConfig>
                        }
                      />
                      {/* final catch all, there's a few route options that could trigger this */}
                      <Route path="*" element={<NotFound />} />
                    </Routes>
                  </LocaleProvider>
                </Auth0ProviderWithHistory>
              </ConfigProvider>
            </ConfirmProvider>
          </ThemeProvider>
        </StyledEngineProvider>
      </GlobalLoader.Provider>
    </ErrorBoundary>
  </Router>,
);
